import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  X,
  Save,
  Loader2,
  Lock,
  Settings,
  Clock,
  Target,
  Shuffle,
  MessageSquare,
  Eye,
  RefreshCw,
  Type,
  Image as ImageIcon,
  Hash,
  Info,
  Trash2,
  ExternalLink,
  ListChecks,
  AlertTriangle,
  CalendarClock,
  Award,
  Trophy,
  Medal,
  ScrollText
} from 'lucide-react';
import CertificateTemplateSelect from '@/components/certificate-builder/CertificateTemplateSelect';
import { CertificateTemplateType } from '@/types/certificate-template.types';
import toast from 'react-hot-toast';
import {
  Certification,
  CertificationExamQuestion,
  TierConfig,
  UpdateExamSettingsDto,
  certificationService
} from '@/services/certification.service';
import { useForm } from 'react-hook-form';
import { FileUploadField, TagsField } from '@/components/common/form-fields';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { isCleanText } from '@/utils/sanitize';

const CLEAN_TEXT_ERROR = 'Please use only letters, numbers and standard punctuation';

interface BasicInfoFormData {
  title: string;
  description: string;
}

// A disabled fieldset stops form controls but not drop targets, and FileUploadField
// accepts drag-and-drop on a plain div. Swallowing these in the capture phase keeps
// a published certification from taking an upload.
const swallowDragEvent = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

// Per-difficulty question counts served to learners. Absent or disabled means
// the exam keeps using every question (legacy behaviour).
interface QuestionSelectionConfig {
  enabled: boolean;
  easy_count: number;
  medium_count: number;
  hard_count: number;
  total_count: number;
}

type DifficultyKey = 'easy' | 'medium' | 'hard';

const DIFFICULTY_ROWS: { key: DifficultyKey; label: string; dot: string; bar: string; ring: string }[] = [
  { key: 'easy', label: 'Easy', dot: 'bg-emerald-500', bar: 'bg-emerald-500', ring: 'focus:ring-emerald-500 focus:border-emerald-500' },
  { key: 'medium', label: 'Medium', dot: 'bg-amber-500', bar: 'bg-amber-500', ring: 'focus:ring-amber-500 focus:border-amber-500' },
  { key: 'hard', label: 'Hard', dot: 'bg-rose-500', bar: 'bg-rose-500', ring: 'focus:ring-rose-500 focus:border-rose-500' },
];

interface EditCertificationModalProps {
  certification: Certification;
  certificationId?: string;
  onClose: () => void;
  onSuccess: () => void;
  /** Requires the admin_certification_delete permission. Defaults to false so the
   *  delete action stays closed unless a caller explicitly grants it. */
  canDelete?: boolean;
  /** Published certifications are frozen: settings stay visible but read-only.
   *  Deleting is unaffected — that is governed by canDelete alone. */
  isPublished?: boolean;
}

// Reusable Toggle Switch component
function ToggleSwitch({
  checked,
  onChange,
  color = 'blue',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  color?: 'blue' | 'yellow' | 'emerald' | 'purple';
}) {
  const colorMap = {
    blue: 'peer-checked:bg-brand-blue-600 peer-focus:ring-brand-blue-300',
    yellow: 'peer-checked:bg-brand-yellow-500 peer-focus:ring-brand-yellow-300',
    emerald: 'peer-checked:bg-brand-emerald-600 peer-focus:ring-brand-emerald-300',
    purple: 'peer-checked:bg-brand-purple-600 peer-focus:ring-brand-purple-300',
  };

  return (
    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div
        className={`w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm ${colorMap[color]}`}
      />
    </label>
  );
}

// Reusable Toggle Row component
function ToggleRow({
  icon: Icon,
  iconColor,
  label,
  description,
  checked,
  onChange,
  toggleColor = 'blue',
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  toggleColor?: 'blue' | 'yellow' | 'emerald' | 'purple';
}) {
  return (
    <div
      className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
        checked
          ? 'bg-brand-blue-50/50 border-brand-blue-200 shadow-sm'
          : 'bg-gray-50/80 border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className={`flex-shrink-0 p-2 rounded-lg ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <label className="text-sm font-semibold text-gray-900 cursor-pointer">{label}</label>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} color={toggleColor} />
    </div>
  );
}

export default function EditCertificationModal({
  certification,
  certificationId,
  onClose,
  onSuccess,
  canDelete = false,
  isPublished = false
}: EditCertificationModalProps) {
  console.log("certification", certification)
  const targetId = certificationId || certification._id;
  const initial_passing_score = certification.passing_score || certification.exam?.passing_score || 70;
  const loaded_tier_config = certification.tier_config ?? certification.exam?.tier_config;
  const [formData, setFormData] = useState<UpdateExamSettingsDto>({
    title: certification.exam?.title || certification.title || '',
    description: certification.exam?.description || certification.description || '',
    tags: certification.tags || [],
    banner_image_url: certification.banner_image_url || certification.exam?.banner_image_url || '',
    thumbnail_image_url: certification.thumbnail_image_url || certification.exam?.thumbnail_image_url || '',
    time_limit_minutes: certification.time_limit_minutes || certification.exam?.time_limit_minutes,
    passing_score: initial_passing_score,
    shuffle_questions: certification?.shuffle_questions ?? false,
    shuffle_options: certification?.shuffle_options ?? false,
    show_feedback: certification?.show_feedback ?? true,
    show_correct_answers: certification?.show_correct_answers ?? true,
    allow_retry: certification.allow_retry ?? false,
    max_attempts: certification?.max_attempts,
    // The details endpoint returns the exam document, so tier_config sits at the top level.
    // Left undefined when not loaded so a save never overwrites the stored tiers with defaults.
    // Bronze always equals the passing score, so a pass always earns at least Bronze.
    tier_config: loaded_tier_config ? { ...loaded_tier_config, bronze_threshold: initial_passing_score } : undefined,
  });

  const [activeSection, setActiveSection] = useState<string>('basic');

  // Title/description use the shared Input/Textarea (clean-text check + RTL), which need react-hook-form
  const { control: basicInfoControl, getValues: getBasicInfo, setError: setBasicInfoError } = useForm<BasicInfoFormData>({
    defaultValues: { title: formData.title || '', description: formData.description || '' },
  });

  const availableCounts = useMemo(() => {
    const questions = ((certification.questions || certification.exam?.questions || []) as CertificationExamQuestion[])
      .filter((q) => !(q as any).is_deleted);
    const counts = { total: questions.length, easy: 0, medium: 0, hard: 0, unspecified: 0 };
    questions.forEach((q) => {
      const level = (q.difficulty_level || '').toString().toLowerCase();
      if (level === 'easy' || level === 'medium' || level === 'hard') counts[level] += 1;
      else counts.unspecified += 1;
    });
    return counts;
  }, [certification]);

  const savedSelection: QuestionSelectionConfig | undefined =
    (certification as any).question_selection ?? (certification.exam as any)?.question_selection;

  const [selectionEnabled, setSelectionEnabled] = useState<boolean>(savedSelection?.enabled ?? false);

  // Kept as strings so the field can be cleared while typing. Saved counts are not clamped,
  // so questions deleted after configuring surface as inline errors.
  const [countInputs, setCountInputs] = useState<Record<DifficultyKey, string>>(() => ({
    easy: String(savedSelection?.easy_count ?? availableCounts.easy),
    medium: String(savedSelection?.medium_count ?? availableCounts.medium),
    hard: String(savedSelection?.hard_count ?? availableCounts.hard),
  }));

  const parsedCounts: Record<DifficultyKey, number> = {
    easy: /^\d+$/.test(countInputs.easy) ? parseInt(countInputs.easy, 10) : 0,
    medium: /^\d+$/.test(countInputs.medium) ? parseInt(countInputs.medium, 10) : 0,
    hard: /^\d+$/.test(countInputs.hard) ? parseInt(countInputs.hard, 10) : 0,
  };

  const selectedTotal = parsedCounts.easy + parsedCounts.medium + parsedCounts.hard;

  const countErrors: Partial<Record<DifficultyKey, string>> = {};
  DIFFICULTY_ROWS.forEach(({ key, label }) => {
    const raw = countInputs[key].trim();
    const available = availableCounts[key];
    if (raw === '') return;
    if (!/^\d+$/.test(raw)) countErrors[key] = 'Only whole numbers of 0 or more';
    else if (parsedCounts[key] > available) {
      countErrors[key] = available === 0
        ? `No ${label.toLowerCase()} questions available, keep it 0`
        : `Only ${available} available`;
    }
  });

  const hasCountErrors = Object.keys(countErrors).length > 0;
  const totalError = !hasCountErrors && selectedTotal < 1 ? 'Select at least one question' : '';
  const hasSelectionError = selectionEnabled && (hasCountErrors || !!totalError);

  const updateDifficultyCount = (key: DifficultyKey, rawValue: string) => {
    setCountInputs(prev => ({ ...prev, [key]: rawValue.replace(/[^\d]/g, '') }));
  };

  const blockNonDigitKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) e.preventDefault();
  };

  // Validity lives on the certification record (this modal edits the exam), so load and save it separately.
  type Validity = { type: 'LIFETIME' | 'FIXED_DURATION'; months: string };
  const { data: certificationRecord, isError: validityLoadFailed } = useQuery({
    queryKey: ['certification-record', certificationId],
    queryFn: () => certificationService.getCertificationById(certificationId!),
    enabled: !!certificationId,
    gcTime: 0, // always fetch fresh when the dialog opens, never show a cached old value
  });
  const [savedValidity, setSavedValidity] = useState<Validity | null>(null);
  const [validity, setValidity] = useState<Validity>({ type: 'LIFETIME', months: '12' });
  useEffect(() => {
    if (!certificationRecord || savedValidity) return;
    const loaded: Validity = certificationRecord.validity_type === 'FIXED_DURATION'
      ? { type: 'FIXED_DURATION', months: String(certificationRecord.validity_months ?? 12) }
      : { type: 'LIFETIME', months: '12' };
    setSavedValidity(loaded);
    setValidity(loaded);
  }, [certificationRecord, savedValidity]);
  const validityChanged = !!savedValidity && (
    validity.type !== savedValidity.type ||
    (validity.type === 'FIXED_DURATION' && validity.months !== savedValidity.months)
  );

  // Certificate template per tier, also on the certification record ('' = tier's default template)
  type TemplateChoice = { GOLD: string; SILVER: string; BRONZE: string };
  const [savedTemplates, setSavedTemplates] = useState<TemplateChoice | null>(null);
  const [templateChoice, setTemplateChoice] = useState<TemplateChoice>({ GOLD: '', SILVER: '', BRONZE: '' });
  useEffect(() => {
    if (!certificationRecord || savedTemplates) return;
    const ids = certificationRecord.certificate_template_ids || {};
    const loaded: TemplateChoice = { GOLD: ids.GOLD || '', SILVER: ids.SILVER || '', BRONZE: ids.BRONZE || '' };
    setSavedTemplates(loaded);
    setTemplateChoice(loaded);
  }, [certificationRecord, savedTemplates]);
  const templatesChanged = !!savedTemplates && (['GOLD', 'SILVER', 'BRONZE'] as const)
    .some(tier => templateChoice[tier] !== savedTemplates[tier]);
  const setTierTemplate = useMemo(() => ({
    GOLD: (id: string) => setTemplateChoice(prev => ({ ...prev, GOLD: id })),
    SILVER: (id: string) => setTemplateChoice(prev => ({ ...prev, SILVER: id })),
    BRONZE: (id: string) => setTemplateChoice(prev => ({ ...prev, BRONZE: id }))
  }), []);

  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: UpdateExamSettingsDto) => {
      // Templates first: if a chosen template was deleted meanwhile, the save stops before the other
      // settings change. Both calls are safe to repeat, so retrying after any failure is fine.
      if (certificationId && templatesChanged) {
        // Only chosen tiers are sent; a missing tier means "use the default"
        const ids = Object.fromEntries(Object.entries(templateChoice).filter(([, id]) => id));
        await certificationService.updateCertificationTemplates(certificationId, ids);
      }
      const exam = await certificationService.updateExamSettings(certification._id, settings);
      if (certificationId && validityChanged) {
        await certificationService.updateCertificationValidity(certificationId, validity.type === 'FIXED_DURATION'
          ? { validity_type: 'FIXED_DURATION', validity_months: Number(validity.months) }
          : { validity_type: 'LIFETIME' });
      }
      return exam;
    },
    onSuccess: () => {
      toast.success('Certification settings updated successfully!');
      onSuccess();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;

      if (errorData?.meta_data?.errors && Array.isArray(errorData.meta_data.errors)) {
        errorData.meta_data.errors.forEach((err: string) => {
          toast.error(err, { duration: 5000 });
        });
      } else if (errorData?.error?.error_description) {
        toast.error(errorData.error.error_description);
      } else if (errorData?.error_description) {
        toast.error(errorData.error_description);
      } else {
        toast.error('Failed to update certification settings');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Checked here too: the fields may be unmounted while another section is open
    const { title, description } = getBasicInfo();
    if (!title?.trim()) {
      setBasicInfoError('title', { message: 'Title is required' });
      setActiveSection('basic');
      toast.error('Title is required');
      return;
    }
    const invalidFields = (['title', 'description'] as const).filter(field => !isCleanText(getBasicInfo(field)));
    if (invalidFields.length > 0) {
      invalidFields.forEach(field => setBasicInfoError(field, { message: CLEAN_TEXT_ERROR }));
      setActiveSection('basic');
      toast.error(CLEAN_TEXT_ERROR);
      return;
    }

    if (formData.passing_score && (formData.passing_score < 0 || formData.passing_score > 100)) {
      toast.error('Passing score must be between 0 and 100');
      return;
    }

    if (formData.time_limit_minutes && formData.time_limit_minutes < 0) {
      toast.error('Time limit must be a positive number');
      return;
    }

    if (formData.allow_retry && formData.max_attempts !== undefined && formData.max_attempts !== null && formData.max_attempts <= 0) {
      toast.error('Maximum attempts must be a positive number');
      return;
    }

    if (formData.tier_config?.enabled) {
      const { gold_threshold, silver_threshold, bronze_threshold } = formData.tier_config;
      const passing = formData.passing_score ?? 0;
      const missing = (v: any) => v === undefined || v === null || isNaN(Number(v));
      if (missing(gold_threshold) || missing(silver_threshold) || missing(bronze_threshold)) {
        toast.error('Please enter the Gold, Silver and Bronze thresholds');
        return;
      }
      if ([gold_threshold, silver_threshold, bronze_threshold].some(v => v! < 0 || v! > 100)) {
        toast.error('Thresholds must be between 0 and 100');
        return;
      }
      if (bronze_threshold !== passing) {
        toast.error(`Bronze threshold must equal the passing score (${passing}%)`);
        return;
      }
      if (gold_threshold! <= silver_threshold!) {
        toast.error('Gold threshold must be greater than Silver threshold');
        return;
      }
      if (silver_threshold! <= bronze_threshold!) {
        toast.error('Silver threshold must be greater than Bronze threshold');
        return;
      }
    }

    if (hasSelectionError) {
      toast.error('Fix the question selection errors before saving');
      setActiveSection('exam');
      return;
    }

    if (validity.type === 'FIXED_DURATION') {
      const months = Number(validity.months);
      if (!validity.months.trim() || !Number.isInteger(months) || months < 1 || months > 120) {
        setActiveSection('exam');
        toast.error('Certificate validity must be a whole number of months between 1 and 120');
        return;
      }
    }

    const questionSelection: QuestionSelectionConfig = {
      enabled: selectionEnabled,
      easy_count: parsedCounts.easy,
      medium_count: parsedCounts.medium,
      hard_count: parsedCounts.hard,
      total_count: selectedTotal,
    };

    const submitData = {
      ...formData,
      title,
      description,
      max_attempts: formData.allow_retry
        ? (formData.max_attempts || null)
        : 1,
      // Unconfigured certifications omit the field so they keep the all-questions flow.
      ...(selectionEnabled || savedSelection ? { question_selection: questionSelection } : {})
    };

    updateSettingsMutation.mutate(submitData);
  };

  const navigate = useNavigate();
  const [confirmDeleteChecked, setConfirmDeleteChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: Type },
    { id: 'assets', label: 'Brand Assets', icon: ImageIcon },
    { id: 'exam', label: 'Exam Config', icon: Settings },
    { id: 'display', label: 'Display', icon: Eye },
    { id: 'tiers', label: 'Tiers', icon: Trophy },
    ...(certificationId ? [{ id: 'certificate', label: 'Certificate', icon: ScrollText }] : []),
  ];

  // --- Tier threshold real-time validation ---
  // Each tier must be ≤ 100, ≥ the passing score, and ordered Gold > Silver > Bronze.
  // Fields start EMPTY (not 0) so the user can clear/type freely; an out-of-range value is
  // flagged with a message rather than being silently changed; errors only show once a value
  // is entered.
  const tc = formData.tier_config;
  const passing_val = formData.passing_score ?? 0;
  // Exams saved without tier_config are graded with these defaults, so show them.
  // Nothing is sent on save unless the admin edits a tier value.
  const tier_view = tc ?? { enabled: true, gold_threshold: 90, silver_threshold: 75, bronze_threshold: passing_val };
  const editTiers = (changes: Partial<TierConfig>) => setFormData(prev => ({
    ...prev,
    tier_config: {
      enabled: true,
      gold_threshold: 90,
      silver_threshold: 75,
      ...prev.tier_config,
      bronze_threshold: prev.passing_score ?? 70,
      ...changes
    }
  }));
  const gold_v = tc?.gold_threshold;
  const silver_v = tc?.silver_threshold;
  const bronze_v = tc?.bronze_threshold;
  const has = (v?: number): v is number => v !== undefined && v !== null && !isNaN(v as number);
  const gold_error = !has(gold_v) ? ''
    : gold_v > 100 ? 'Maximum is 100%'
    : gold_v < passing_val ? `Must be ≥ passing score (${passing_val}%)` : '';
  const silver_error = !has(silver_v) ? ''
    : silver_v > 100 ? 'Maximum is 100%'
    : silver_v < passing_val ? `Must be ≥ passing score (${passing_val}%)`
    : (has(gold_v) && silver_v >= gold_v) ? `Must be less than Gold (${gold_v}%)` : '';
  const bronze_error = !has(bronze_v) ? ''
    : bronze_v > 100 ? 'Maximum is 100%'
    : bronze_v < passing_val ? `Must be ≥ passing score (${passing_val}%)`
    : (has(silver_v) && bronze_v >= silver_v) ? `Must be less than Silver (${silver_v}%)` : '';
  // When tiers are enabled, all three thresholds must be filled in.
  const tiers_incomplete = !!(tc?.enabled && (!has(gold_v) || !has(silver_v) || !has(bronze_v)));
  const has_tier_error = !!(tc?.enabled && (gold_error || silver_error || bronze_error)) || tiers_incomplete;

  // Parse input: blank → undefined (so the field can be cleared); otherwise its numeric value
  // (no auto-clamping — an out-of-range value is shown to the user and flagged, not rewritten).
  const parseThreshold = (raw: string): number | undefined =>
    raw === '' ? undefined : Number(raw);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200 common-modal-overlay">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-white rounded-t-xl border-b px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-xl">
              <Settings className="w-6 h-6 text-gray-700" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Edit Certification Settings</h2>
              <p className="text-gray-500 text-xs mt-0.5">Configure exam parameters and display options</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          {/* Sections scroll on their own; Delete is pinned to the sidebar's bottom so it never shifts between sections */}
          <div className="md:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r bg-gray-50 flex md:flex-col overflow-hidden">
            <nav className="flex-1 min-w-0 overflow-x-auto md:overflow-x-visible md:overflow-y-auto p-4">
              <div className="flex md:flex-col gap-1.5">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ${isActive
                        ? 'bg-brand-blue-600 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </div>
            </nav>

            <div className="flex-shrink-0 p-4">
              <button
                type="button"
                disabled={!canDelete}
                title={!canDelete ? 'You do not have permission to delete certifications' : undefined}
                onClick={() => {
                  setActiveSection('delete');
                  setConfirmDeleteChecked(false);
                  setActiveUsers([]);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${activeSection === 'delete'
                  ? 'bg-brand-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                <span>Delete Certification</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Editable settings. A published certification is frozen, so these
                  stay visible but disabled — the delete section below is
                  deliberately outside this fieldset. */}
              <fieldset
                disabled={isPublished}
                onDragOverCapture={isPublished ? swallowDragEvent : undefined}
                onDropCapture={isPublished ? swallowDragEvent : undefined}
                className={isPublished ? 'opacity-60 cursor-not-allowed [&_*]:cursor-not-allowed' : undefined}
              >
              {isPublished && activeSection !== 'delete' && (
                <div className="mb-4 flex items-start gap-2.5 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <Lock className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">
                    This certification is published, so its settings are read-only.
                  </p>
                </div>
              )}
              {/* === BASIC INFORMATION === */}
              {activeSection === 'basic' && (
                <section className="space-y-5 animate-in fade-in duration-200">
                  {/* <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 bg-brand-blue-100 rounded-xl">
                      <Type className="w-5 h-5 text-brand-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Basic Information</h3>
                      <p className="text-xs text-gray-500">Title, description, and tags</p>
                    </div>
                  </div> */}

                  <div className="space-y-4 pl-0.5">
                    {/* Title */}
                    <Input<BasicInfoFormData>
                      name="title"
                      control={basicInfoControl}
                      label="Certification Title"
                      required
                      type="text"
                      rules={{ validate: { required: (v: string) => !!v?.trim() || 'Title is required' } }}
                      placeholder="Enter certification title"
                    />

                    {/* Description */}
                    <Textarea<BasicInfoFormData>
                      name="description"
                      control={basicInfoControl}
                      label="Description"
                      rows={5}
                      placeholder="Describe what this certification covers..."
                    />

                    {/* Tags */}
                    <div>
                      <TagsField
                        label="Tags"
                        description="Add relevant tags to help categorize this certification"
                        value={formData.tags || []}
                        onChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                        placeholder="Add a tag (e.g., JavaScript, Beginner, Web Development)"
                        maxTags={10}
                      />
                    </div>
                  </div>
                </section>
              )}

              {/* === BRAND ASSETS === */}
              {activeSection === 'assets' && (
                <section className="space-y-5 animate-in fade-in duration-200">
                  {/* <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 bg-brand-pink-100 rounded-xl">
                      <ImageIcon className="w-5 h-5 text-brand-pink-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Brand Assets</h3>
                      <p className="text-xs text-gray-500">Upload banner and thumbnail images</p>
                    </div>
                  </div> */}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FileUploadField
                      label="Banner Image"
                      description="Displayed as the header image (Recommended: 1200x400px)"
                      currentFileUrl={formData.banner_image_url}
                      onFileChange={(url) => setFormData(prev => ({ ...prev, banner_image_url: url || '' }))}
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={10}
                      metadata={{ category: 'certification', type: 'banner' }}
                    />
                    <FileUploadField
                      label="Thumbnail Image"
                      description="Displayed in certification lists (Recommended: 400x400px)"
                      currentFileUrl={formData.thumbnail_image_url}
                      onFileChange={(url) => setFormData(prev => ({ ...prev, thumbnail_image_url: url || '' }))}
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={10}
                      metadata={{ category: 'certification', type: 'thumbnail' }}
                    />
                  </div>
                </section>
              )}

              {/* === EXAM CONFIGURATION === */}
              {activeSection === 'exam' && (
                <section className="space-y-5 animate-in fade-in duration-200">

                  {/* Time & Score Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:border-gray-300 transition-colors">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <Clock className="w-4 h-4 text-brand-amber-500" />
                        Time Limit (minutes)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.time_limit_minutes || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          time_limit_minutes: e.target.value ? parseInt(e.target.value) : undefined
                        }))}
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 hover:border-gray-400 transition-shadow"
                        placeholder="No limit"
                      />
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Leave empty for unlimited time
                      </p>
                    </div>

                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:border-gray-300 transition-colors">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <Target className="w-4 h-4 text-rose-500" />
                        Passing Score (%)
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.passing_score}
                        onChange={(e) => {
                          const passing_score = parseInt(e.target.value) || 0;
                          setFormData(prev => ({
                            ...prev,
                            passing_score,
                            tier_config: prev.tier_config ? { ...prev.tier_config, bronze_threshold: passing_score } : prev.tier_config
                          }));
                        }}
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 hover:border-gray-400 transition-shadow"
                        placeholder="e.g., 70"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Minimum score to pass the certification
                      </p>
                    </div>
                  </div>

                  {/* Retry Settings */}
                  <div className="space-y-3">
                    <ToggleRow
                      icon={RefreshCw}
                      iconColor="bg-purple-100 text-purple-600"
                      label="Allow Retry"
                      description="Learners can retake the exam if they don't pass"
                      checked={formData.allow_retry ?? false}
                      onChange={(checked) => setFormData(prev => ({ ...prev, allow_retry: checked }))}
                      toggleColor="purple"
                    />

                    {formData.allow_retry && (
                      <div className="mt-3 p-4 rounded-xl border border-brand-purple-200 bg-brand-purple-50/50 animate-in fade-in slide-in-from-top-2 duration-200">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                          <Hash className="w-3.5 h-3.5 text-brand-purple-500" />
                          Maximum Attempts
                          <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.max_attempts || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            max_attempts: e.target.value ? parseInt(e.target.value) : undefined
                          }))}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500 hover:border-gray-400 transition-shadow"
                          placeholder="Unlimited"
                        />
                        <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Leave empty to allow unlimited attempts
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Certificate Validity (saved on the certification record) */}
                  {certificationId && (
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:border-gray-300 transition-colors">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <CalendarClock className="w-4 h-4 text-brand-teal-500" />
                        Certificate Validity
                      </label>
                      {validityLoadFailed ? (
                        <p className="text-xs text-red-500">Could not load the current validity. Reopen this dialog to try again.</p>
                      ) : !savedValidity ? (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                        </p>
                      ) : (
                        <>
                          <div className="flex flex-wrap gap-2">
                            {([
                              { value: 'LIFETIME', label: 'Lifetime' },
                              { value: 'FIXED_DURATION', label: 'Fixed duration' },
                            ] as const).map(option => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setValidity(prev => ({ ...prev, type: option.value }))}
                                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${validity.type === option.value
                                  ? 'bg-brand-teal-50 border-brand-teal-500 text-brand-teal-700'
                                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                          {validity.type === 'FIXED_DURATION' && (
                            <div className="mt-3 flex items-center gap-2 max-w-xs">
                              <input
                                type="number"
                                min="1"
                                max="120"
                                step="1"
                                value={validity.months}
                                onChange={(e) => setValidity(prev => ({ ...prev, months: e.target.value }))}
                                className="w-28 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-teal-500 focus:border-brand-teal-500 hover:border-gray-400 transition-shadow"
                                placeholder="12"
                              />
                              <span className="text-sm text-gray-600">months</span>
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            {validity.type === 'FIXED_DURATION'
                              ? 'Certificates expire this many months after issue (12 months = 1 year). Applies to certificates issued after saving.'
                              : 'Certificates never expire.'}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* === QUESTION SELECTION (part of Exam Config) === */}
              {activeSection === 'exam' && (
                <section className="space-y-5 mt-6 pt-6 border-t border-gray-200 animate-in fade-in duration-200">
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-brand-indigo-600" />
                    <h3 className="text-sm font-bold text-gray-900">Question Selection</h3>
                  </div>

                  {/* Question bank overview */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="p-4 rounded-xl border border-brand-blue-200 bg-brand-blue-50/50">
                      <p className="text-xs font-semibold text-brand-blue-700 uppercase tracking-wide">Total Questions</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{availableCounts.total}</p>
                      <p className="text-xs text-gray-500 mt-0.5">In question bank</p>
                    </div>
                    {DIFFICULTY_ROWS.map((row) => (
                      <div key={row.key} className="p-4 rounded-xl border border-gray-200 bg-gray-50/50">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          <span className={`w-2 h-2 rounded-full ${row.dot}`} />
                          {row.label}
                        </p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{availableCounts[row.key]}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Available</p>
                      </div>
                    ))}
                  </div>

                  {availableCounts.unspecified > 0 && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-900">
                        {availableCounts.unspecified} question(s) have no difficulty level and will not be picked when a custom selection is enabled.
                      </p>
                    </div>
                  )}

                  <ToggleRow
                    icon={ListChecks}
                    iconColor="bg-indigo-100 text-indigo-600"
                    label="Custom Question Selection"
                    description="Serve a fixed number of questions per difficulty on each attempt. When off, learners get every question in the bank."
                    checked={selectionEnabled}
                    onChange={setSelectionEnabled}
                  />

                  {selectionEnabled && (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {DIFFICULTY_ROWS.map((row) => {
                          const available = availableCounts[row.key];
                          const error = countErrors[row.key];
                          return (
                            <div
                              key={row.key}
                              className={`p-4 rounded-xl border transition-colors ${error ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'}`}
                            >
                              <label className="flex items-center justify-between text-sm font-semibold text-gray-700 mb-3">
                                <span className="flex items-center gap-2">
                                  <span className={`w-2.5 h-2.5 rounded-full ${row.dot}`} />
                                  {row.label} Questions
                                </span>
                                <span className="text-xs font-normal text-gray-400">max {available}</span>
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={countInputs[row.key]}
                                onKeyDown={blockNonDigitKeys}
                                onChange={(e) => updateDifficultyCount(row.key, e.target.value)}
                                placeholder="0"
                                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 hover:border-gray-400 transition-shadow ${error ? 'border-red-400' : 'border-gray-300'} ${row.ring}`}
                              />
                              <div className="flex items-center justify-between gap-2 mt-2">
                                {error ? (
                                  <p className="text-xs text-red-600">{error}</p>
                                ) : (
                                  <p className="text-xs text-gray-400">{parsedCounts[row.key]} of {available}</p>
                                )}
                                {available > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => updateDifficultyCount(row.key, String(available))}
                                    className="text-xs font-semibold text-brand-blue-600 hover:text-brand-blue-700 flex-shrink-0"
                                  >
                                    Use all
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Selected total */}
                      <div className="p-4 rounded-xl border border-brand-indigo-200 bg-brand-indigo-50/50">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-gray-700">Questions per attempt</p>
                          <p className="text-sm text-gray-600">
                            <span className="text-2xl font-bold text-gray-900">{selectedTotal}</span>
                            <span className="ml-1">of {availableCounts.total}</span>
                          </p>
                        </div>
                        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
                          {DIFFICULTY_ROWS.map((row) => {
                            const width = availableCounts.total > 0 ? (parsedCounts[row.key] / availableCounts.total) * 100 : 0;
                            return <div key={row.key} className={row.bar} style={{ width: `${Math.min(width, 100)}%` }} />;
                          })}
                        </div>
                        {totalError && (
                          <p className="text-xs text-red-600 mt-2">{totalError}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Questions are picked at random per difficulty for each attempt; the pass mark applies to the selected questions only.
                        </p>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* === DISPLAY SETTINGS === */}
              {activeSection === 'display' && (
                <section className="space-y-5 animate-in fade-in duration-200">

                  <div className="space-y-3">
                    <ToggleRow
                      icon={Shuffle}
                      iconColor="bg-sky-100 text-sky-600"
                      label="Shuffle Questions"
                      description="Randomize question order for each attempt"
                      checked={formData.shuffle_questions ?? false}
                      onChange={(checked) => setFormData(prev => ({ ...prev, shuffle_questions: checked }))}
                    />
                    <ToggleRow
                      icon={Shuffle}
                      iconColor="bg-violet-100 text-violet-600"
                      label="Shuffle Options"
                      description="Randomize answer options within each question"
                      checked={formData.shuffle_options ?? false}
                      onChange={(checked) => setFormData(prev => ({ ...prev, shuffle_options: checked }))}
                    />
                    <ToggleRow
                      icon={MessageSquare}
                      iconColor="bg-amber-100 text-amber-600"
                      label="Show Feedback"
                      description="Display explanations after exam submission"
                      checked={formData.show_feedback ?? true}
                      onChange={(checked) => setFormData(prev => ({ ...prev, show_feedback: checked }))}
                    />
                    <ToggleRow
                      icon={Eye}
                      iconColor="bg-teal-100 text-teal-600"
                      label="Show Correct Answers"
                      description="Reveal correct answers after exam submission"
                      checked={formData.show_correct_answers ?? true}
                      onChange={(checked) => setFormData(prev => ({ ...prev, show_correct_answers: checked }))}
                    />
                  </div>
                </section>
              )}

              {/* === TIER CONFIGURATION === */}
              {activeSection === 'tiers' && (
              <section className="space-y-5 animate-in fade-in duration-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-xl">
                    <Trophy className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Tier Configuration</h3>
                    <p className="text-xs text-gray-500">Award Gold, Silver, or Bronze based on exam score. When disabled, this becomes a practice test — no certificate is issued.</p>
                  </div>
                </div>

                <ToggleRow
                  icon={Award}
                  iconColor="bg-yellow-100 text-yellow-600"
                  label="Enable Achievement Tiers"
                  description="Award tier badges based on final exam scores. Turn off for a practice test (no certificate issued)."
                  checked={tier_view.enabled}
                  onChange={(checked) => editTiers({ enabled: checked })}
                  toggleColor="yellow"
                />

                {tier_view.enabled && (
                  <div className="space-y-5 animate-in fade-in slide-in-from-top-2 duration-200">

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                      <div className="relative p-4 rounded-xl border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-amber-50 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-yellow-400 rounded-lg shadow-sm">
                            <Trophy className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-bold text-yellow-800">Gold</span>
                        </div>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={tier_view.gold_threshold ?? ''}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => editTiers({ gold_threshold: parseThreshold(e.target.value) as number })}
                            className={`w-full border rounded-xl pl-4 pr-9 py-2.5 text-sm font-semibold focus:ring-2 bg-white [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${gold_error ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-yellow-300 focus:ring-yellow-400 focus:border-yellow-400'}`}
                            placeholder="90"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 pointer-events-none">%</span>
                        </div>
                        {gold_error
                          ? <p className="text-xs text-red-600 mt-2 font-medium">{gold_error}</p>
                          : <p className="text-xs text-yellow-700/70 mt-2">Score at or above this earns Gold</p>}
                      </div>


                      <div className="relative p-4 rounded-xl border-2 border-gray-300 bg-gradient-to-br from-gray-50 to-slate-50 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-gray-400 rounded-lg shadow-sm">
                            <Medal className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-bold text-gray-700">Silver</span>
                        </div>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={tier_view.silver_threshold ?? ''}
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => editTiers({ silver_threshold: parseThreshold(e.target.value) as number })}
                            className={`w-full border rounded-xl pl-4 pr-9 py-2.5 text-sm font-semibold focus:ring-2 bg-white [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${silver_error ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-gray-400 focus:border-gray-400'}`}
                            placeholder="75"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 pointer-events-none">%</span>
                        </div>
                        {silver_error
                          ? <p className="text-xs text-red-600 mt-2 font-medium">{silver_error}</p>
                          : <p className="text-xs text-gray-500 mt-2">Score at or above this earns Silver</p>}
                      </div>


                      <div className="relative p-4 rounded-xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-amber-600 rounded-lg shadow-sm">
                            <Award className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-bold text-amber-800">Bronze</span>
                        </div>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={tier_view.bronze_threshold ?? ''}
                            readOnly
                            tabIndex={-1}
                            title="Bronze always equals the passing score"
                            className={`w-full border rounded-xl pl-4 pr-9 py-2.5 text-sm font-semibold focus:ring-2 bg-gray-100 text-gray-600 cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${bronze_error ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-amber-300 focus:ring-amber-500 focus:border-amber-500'}`}
                            placeholder="60"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 pointer-events-none">%</span>
                        </div>
                        {bronze_error
                          ? <p className="text-xs text-red-600 mt-2 font-medium">{bronze_error}</p>
                          : <p className="text-xs text-amber-700/70 mt-2">Same as the passing score — any pass earns Bronze</p>}
                      </div>
                    </div>

                  </div>
                )}
              </section>
              )}
              {/* === END TIER CONFIGURATION === */}

              {/* === CERTIFICATE TEMPLATES (saved on the certification record) === */}
              {activeSection === 'certificate' && certificationId && (
              <section className="space-y-5 animate-in fade-in duration-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-indigo-100 rounded-xl">
                    <ScrollText className="w-5 h-5 text-brand-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Certificate Templates</h3>
                    <p className="text-xs text-gray-500">Choose the certificate learners receive for each tier.</p>
                  </div>
                </div>

                {!tier_view.enabled && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    Tiers are turned off, so this certification works as a practice test and no certificate is issued.
                  </p>
                )}

                {validityLoadFailed ? (
                  <p className="text-xs text-red-500">Could not load the current settings. Reopen this dialog to try again.</p>
                ) : !savedTemplates ? (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
                      {([
                        { tier: 'GOLD', label: 'Gold', type: CertificateTemplateType.CERTIFICATION_GOLD, icon: Trophy, iconColor: 'text-yellow-600 bg-yellow-50' },
                        { tier: 'SILVER', label: 'Silver', type: CertificateTemplateType.CERTIFICATION_SILVER, icon: Medal, iconColor: 'text-gray-500 bg-gray-100' },
                        { tier: 'BRONZE', label: 'Bronze', type: CertificateTemplateType.CERTIFICATION_BRONZE, icon: Award, iconColor: 'text-amber-700 bg-amber-50' }
                      ] as const).map(row => (
                        <div key={row.tier} className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-2 sm:gap-4 p-4">
                          <div className="flex items-center gap-2 sm:h-[42px]">
                            <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${row.iconColor}`}>
                              <row.icon className="w-4 h-4" />
                            </span>
                            <span className="text-sm font-semibold text-gray-800">{row.label} tier</span>
                          </div>
                          <CertificateTemplateSelect
                            templateType={row.type}
                            value={templateChoice[row.tier]}
                            onChange={setTierTemplate[row.tier]}
                            disabled={isPublished}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Applies to certificates issued after saving. Already issued certificates keep their design until regenerated.
                    </p>
                  </div>
                )}
              </section>
              )}
              </fieldset>

              {/* === DELETE CERTIFICATION === */}
              {activeSection === 'delete' && (
                
                <section className="space-y-6 max-w-2xl mx-auto py-2 animate-in fade-in duration-200">
                  <div className="border border-red-100 rounded-xl p-5 bg-red-50/5">
                    <div className="max-w-xl mx-auto text-start">
                      <div className="flex items-start gap-5 mb-6">
                        {/* Icon */}
                        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Trash2 className="w-7 h-7 text-red-600" />
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Delete Certification
                          </h3>
                          <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">
                            This action is permanent and cannot be undone. All user certificates, exam results, and progress metrics linked to this certification will also be affected.
                          </p>
                        </div>
                      </div>

                      {/* Confirmation Checkbox */}
                      <div className="flex items-start gap-3 text-left">
                        <input
                          type="checkbox"
                          id="confirm-delete-cert-checkbox"
                          checked={confirmDeleteChecked}
                          onChange={(e) => setConfirmDeleteChecked(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                        />
                        <label
                          htmlFor="confirm-delete-cert-checkbox"
                          className="text-xs text-gray-700 select-none cursor-pointer font-medium leading-relaxed"
                        >
                          I confirm that I want to delete this certification. I understand this action is permanent.
                        </label>
                      </div>

                      {/* Active Learners Blockers List */}
                      {activeUsers.length > 0 && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-start">
                          <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">
                            Active Learners ({activeUsers.length})
                          </h4>
                          <p className="text-xs text-amber-700 mb-3">
                            This certification cannot be deleted because it is currently assigned to the active learners listed below. Please revoke their assignments first.
                          </p>
                          <div className="max-h-40 overflow-y-auto space-y-2 pr-1">
                            {activeUsers.map((user) => (
                              <div key={user._id} className="flex items-center justify-between p-2 bg-white rounded border border-amber-100 text-xs shadow-sm">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {user.given_name} {user.family_name || ''}
                                  </div>
                                  <div className="text-gray-500 text-[10px]">{user.email}</div>
                                </div>
                                <button
                                  type="button"
                                  disabled={revokingUserId !== null}
                                  onClick={() => {
                                    setRevokingUserId(user._id);
                                    navigate(`/admin/users/${user._id}/certifications/${targetId}/progress`);
                                  }}
                                  className="p-2 text-gray-500 hover:text-brand-blue-600 hover:bg-brand-blue-50 rounded-lg transition-all disabled:opacity-50"
                                  title="Revoke user assignment"
                                >
                                  {revokingUserId === user?._id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <ExternalLink className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex justify-center gap-3 pt-6 mt-6 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSection('basic');
                            setActiveUsers([]);
                          }}
                          className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Keep Certification
                        </button>

                        <button
                          type="button"
                          disabled={!canDelete || !confirmDeleteChecked || isDeleting}
                          title={!canDelete ? 'You do not have permission to delete certifications' : undefined}
                          onClick={async () => {
                            if (!canDelete) return;
                            setIsDeleting(true);
                            setActiveUsers([]);
                            try {
                              const res: any = await certificationService.deleteCertification(targetId);
                              const users = res?.meta_data?.active_users || res?.data?.active_users;
                              if (Array.isArray(users) && users.length > 0) {
                                setActiveUsers(users);
                              } else {
                                toast.success('Certification deleted successfully');
                                onClose();
                                navigate('/admin/certifications');
                              }
                            } catch (err: any) {
                              console.error('Delete certification failed:', err);
                              const msg =
                                err?.response?.data?.description ||
                                err?.response?.data?.message ||
                                err?.message ||
                                'Failed to delete certification';
                              toast.error(msg);
                            } finally {
                              setIsDeleting(false);
                            }
                          }}
                          className="px-4 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm transition-colors"
                        >
                          {isDeleting ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Delete Certification Permanently</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {activeSection !== 'delete' && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50/80 flex-shrink-0 rounded-b-xl">
            <p className="text-xs text-gray-500 hidden sm:block">
              {isPublished
                ? 'Published certifications are read-only'
                : 'Make sure to save your changes before closing'}
            </p>
            <div className="flex items-center gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 border border-gray-300 rounded-xl font-medium text-sm text-gray-700 hover:bg-gray-100 transition-all hover:border-gray-400"
              >
                {isPublished ? 'Close' : 'Cancel'}
              </button>
              {!isPublished && (
              <button
                type="submit"
                disabled={updateSettingsMutation.isPending || hasSelectionError || has_tier_error}
                title={hasSelectionError ? 'Fix the question selection errors in Exam Config' : undefined}
                className="px-6 py-2.5 bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 hover:from-brand-blue-700 hover:to-brand-indigo-700 text-white rounded-xl font-semibold text-sm shadow-lg shadow-brand-blue-200 hover:shadow-xl hover:shadow-brand-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2"
              >
                {updateSettingsMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
